import { ArrowLeft, Check, Pencil, X } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

interface ChatPanelProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  onBack?: () => void
  headerExtra?: ReactNode
  /** When provided, the header title becomes inline-editable. */
  editableTitle?: { value: string; onSave: (value: string) => void }
  children: ReactNode
}

export function ChatPanel({
  open,
  onClose,
  title,
  onBack,
  headerExtra,
  editableTitle,
  children
}: ChatPanelProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  // Reset edit mode whenever the active view/title changes.
  useEffect(() => {
    setEditing(false)
  }, [title])

  // Close on Escape (unless editing the title, where Escape cancels the edit).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editing) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, editing, onClose])

  if (!open) return null

  const startEdit = () => {
    setDraft(editableTitle?.value ?? '')
    setEditing(true)
  }

  const save = () => {
    const next = draft.trim()
    if (next && next !== editableTitle?.value) editableTitle?.onSave(next)
    setEditing(false)
  }

  return (
    <div
      className={cn(
        'app-chrome fixed z-[1001] flex flex-col overflow-hidden bg-background shadow-[0_24px_60px_rgba(4,33,37,0.28)]',
        'inset-0 rounded-none',
        'sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[640px] sm:max-h-[calc(100vh-2.5rem)] sm:w-[400px] sm:rounded-3xl',
        'duration-300 animate-in fade-in-0 slide-in-from-bottom-4'
      )}
      role="dialog"
      aria-modal="true"
    >
      <header className="flex h-16 shrink-0 items-center gap-2 border-b border-ink/[0.06] bg-white px-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink/70 transition-colors hover:bg-ink/[0.05] hover:text-ink"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}

        {editing && editableTitle ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
              if (e.key === 'Escape') setEditing(false)
            }}
            className="min-w-0 flex-1 rounded-lg border border-input bg-white px-2 py-1 font-display text-base font-bold text-ink focus-visible:border-primary focus-visible:outline-none"
          />
        ) : (
          <h2 className="min-w-0 flex-1 truncate font-display text-base font-bold text-ink">
            {title}
          </h2>
        )}

        {editableTitle && !editing && (
          <button
            type="button"
            onClick={startEdit}
            aria-label="Edit title"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink/60 transition-colors hover:bg-ink/[0.05] hover:text-ink"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        {editing && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={save}
            aria-label="Save title"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10"
          >
            <Check className="h-5 w-5" />
          </button>
        )}

        {!editing && headerExtra}

        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink/70 transition-colors hover:bg-ink/[0.05] hover:text-ink"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  )
}
